import { useCallback, useMemo } from 'react';
import {
  Home,
  ChartBar,
  Info,
  Mail,
  Columns3Cog,
  Clock,
  Globe,
  TestTube,
  Building2,
  Users,
  Wallet,
  FileText,
  Database,
  BookOpen,
  Map,
  Lightbulb,
  ListTree,
  File,
  SearchCheck,
  LayoutDashboard,
} from 'lucide-react';
import type { DashboardConfig } from '@xanectech/components/layout';
import { createLogger } from '@xanectech/lib';
const logger = createLogger('Admin - useAdminDashboardConfig');

import { useAuth } from '@xanectech/auth';
import { useUserProfile } from '@xanectech/auth';
import { useNavigate } from 'react-router-dom';
import {
  useCompany,
  useUnreadMessageCount,
  useMessages,
} from '@xanectech/data';

type AdminDashboardConfigStatus =
  | { status: 'loading' }
  | { status: 'authenticated'; config: DashboardConfig }
  | { status: 'unauthenticated' }
  | {
      status: 'access-denied';
      currentRole?: string | null;
      reason: string;
    };

export function useAdminDashboardConfig(): AdminDashboardConfigStatus {
  const { signOut, user: authUser } = useAuth();
  const { profile, isLoading } = useUserProfile();

  const navigate = useNavigate();

  // Profile is now auto-fetched by useUserProfile when user is authenticated

  // Fetch unread message count
  const { data: unreadCount = 0 } = useUnreadMessageCount(
    String(profile?.userId ?? ''),
    String(profile?.companyId ?? ''),
  );

  const { data: company, isLoading: isLoadingCompany } = useCompany(
    profile?.companyId ?? undefined,
  );

  // Fetch recent messages for notifications
  const { data: messages = [] } = useMessages({
    userId: String(profile?.userId ?? ''),
    companyId: String(profile?.companyId ?? ''),
    userRole: profile?.primaryRole ?? '',
    unreadOnly: false,
  });

  // Convert messages to notifications
  const notifications = useMemo(() => {
    return messages.slice(0, 5).map((message) => ({
      id: message.id,
      title: message.title,
      description: message.content.substring(0, 100),
      timestamp: new Date(String(message.created_at)).toLocaleString(),
      read: message.read,
    }));
  }, [messages]);

  const handleLogout = useCallback(async () => {
    await signOut();
    logger.info('User logged out', { context: 'admin', action: 'logout' });
    void navigate('/logout');
  }, [navigate, signOut]);

  const companyDisplayName = useMemo(() => {
    const rawName = (company?.name ?? profile?.companyName ?? '').trim();
    return rawName.length > 0 ? rawName.split(/\s+/)[0] : '';
  }, [company?.name, profile?.companyName]);

  const companyLogoUrl = useMemo(() => {
    const logo = (company?.logo_url ?? profile?.companyLogoUrl ?? '').trim();
    return logo.length > 0 ? logo : null;
  }, [company?.logo_url, profile?.companyLogoUrl]);

  const hasAdminPortalAccess = useMemo(
    () =>
      Boolean(
        profile?.isActive &&
        (profile?.isInternalAdmin || profile?.isSuperAdmin),
      ),
    [profile?.isActive, profile?.isInternalAdmin, profile?.isSuperAdmin],
  );

  const config = useMemo<DashboardConfig | null>(() => {
    if (!profile || !companyDisplayName || !companyLogoUrl) {
      return null;
    }

    return {
      branding: {
        name: companyDisplayName,
        logo: companyLogoUrl,
        bgColor: 'bg-primary-gradient',
        textColor: 'text-primary',
        logoCollapsed: companyLogoUrl,
      },
      singleExpanded: true,
      basePath: '/',
      navigation: [
        {
          label: 'Main',
          collapsable: false,
          items: [
            { title: 'Dashboard', href: '/', icon: Home },
            { title: 'Settings', href: '/settings', icon: Info },
            {
              title: 'Messages',
              href: '/messages',
              icon: Mail,
              badge: unreadCount > 0 ? unreadCount : undefined,
            },
          ],
        },
        {
          label: 'Management',
          collapsable: true,
          expanded: false,
          items: [
            { title: 'Companies', href: '/companies', icon: Building2 },
            { title: 'Users', href: '/users', icon: Users },
            { title: 'Help', href: '/help-management', icon: BookOpen },
            { title: 'Subscriptions', href: '/subscriptions', icon: Wallet },
            { title: 'Geographical Management', href: '/geo', icon: Globe },
            { title: 'Ideas', href: '/ideas', icon: Lightbulb },
            { title: 'Roadmap', href: '/roadmap', icon: ChartBar },
            {
              title: 'Email Templates',
              href: '/email-templates',
              icon: FileText,
            },
            { title: 'Database', href: '/database', icon: Database },
            { title: 'Logs', href: '/logs', icon: Columns3Cog },
            { title: 'Prototypes', href: '/prototypes', icon: TestTube },
          ],
        },
        {
          label: 'Data Categories',
          collapsable: true,
          expanded: false,
          items: [
            {
              title: 'Data Structures',
              href: '/data-structures',
              icon: Columns3Cog,
            },
          ],
        },
        {
          label: 'Analytics',
          collapsable: true,
          expanded: false,
          items: [
            { title: 'Analytics', href: '/analytics', icon: ChartBar },
            { title: 'Login History', href: '/login-history', icon: Clock },
            // {
            //   title: 'Help Analytics',
            //   href: '/analytics/help',
            //   icon: BookOpen,
            // },
          ],
        },
      ],
      notifications,
      onLogout: handleLogout,
      user: {
        name: `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim(),
        email: profile.email ?? '',
        initials:
          profile.firstName && profile.lastName
            ? `${profile.firstName.charAt(0)}${profile.lastName.charAt(0).toUpperCase()}`
            : '',
      },
    };
  }, [
    companyDisplayName,
    companyLogoUrl,
    handleLogout,
    notifications,
    profile,
    unreadCount,
  ]);

  // If no authenticated user, return unauthenticated
  if (!authUser) {
    return { status: 'unauthenticated' };
  }

  // If authenticated user but profile is still loading, return loading
  if (isLoading) {
    return { status: 'loading' };
  }

  if (profile?.companyId && isLoadingCompany) {
    return { status: 'loading' };
  }

  // If authenticated user but no profile loaded yet, return loading
  if (!profile) {
    return { status: 'loading' };
  }

  if (!hasAdminPortalAccess) {
    logger.warn('User denied admin portal access', {
      currentRole: profile.primaryRole,
      userId: profile.userId,
    });

    return {
      status: 'access-denied',
      currentRole: profile.primaryRole,
      reason: profile.isActive ? 'missing_admin_portal_role' : 'inactive_user',
    };
  }

  if (!config) {
    return { status: 'loading' };
  }

  return {
    status: 'authenticated',
    config,
  };
}
